/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  AlertTriangle, CheckCircle, Calendar, Users, Thermometer,
  ChevronRight, MessageSquare, Clock, ShieldAlert, Wrench, X, Sparkles,
  Coffee, ChefHat, Bug, ChevronDown, Package, ClipboardList, RefreshCw, Star,
} from 'lucide-react';

const C = {
  cream: '#F7F6EF', charcoal: '#2F3A42', navy: '#445366',
  mid: '#7A8A96', border: '#D8DEE3',
  skyBlue: '#BCD9E5', skyBlueLight: '#EAF4F8', skyText: '#3A8FA3',
  green: '#7EA594', greenLight: '#EAF2EF',
  orange: '#E4AC49', orangeLight: '#FDF3DC',
  amber: '#E07B00', amberLight: '#FFF3E0',
  red: '#DC3545', redLight: '#FFF0F0',
  pink: '#F2B8CB', pinkLight: '#FDF0F5', pinkText: '#C4607A',
  white: '#FFFFFF', yellow: '#F7EC71', yellowLight: '#FEFCE8',
  purple: '#7C3AED', purpleLight: '#F3EEFF',
};

const VENUE_IDENTITY = {
  'Box Jordaan':  { bg: '#EDF3FC', text: '#2563EB', border: '#BAD4F5', short: 'Jordaan'  },
  'Box Plantage': { bg: '#C8E6D8', text: '#2D7A65', border: '#7EA594', short: 'Plantage' },
  'Sassys':       { bg: '#EDE0F7', text: '#6D28A0', border: '#6D28A0', short: 'Sassys'   },
  'Prep Kitchen': { bg: C.cream,   text: C.navy,    border: C.border,  short: 'Prep'     },
};

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Sassys is KIT dept — no FOH checklists
const VENUE_DEPT = {
  'Box Jordaan':  ['FOH', 'KIT'],
  'Box Plantage': ['FOH', 'KIT'],
  'Sassys':       ['KIT'],
  'Prep Kitchen': ['KIT'],
};

const PEST_CHECK_VENUES   = ['Box Jordaan', 'Box Plantage', 'Prep Kitchen'];
const FOH_CLEANING_VENUES = ['Box Jordaan', 'Box Plantage'];
// Venues without mid-shift (brunch-only)
const NO_MIDSHIFT_VENUES  = ['Box Plantage'];

// ── Dutch public holidays (computed client-side, same as Calendar.js) ─────────
function getEasterDate(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day); // local midnight — correct
}
function getNLHolidays(year) {
  const fmt = (d) => {
    const yr = d.getFullYear(), mo = d.getMonth() + 1, dy = d.getDate();
    return `${yr}-${String(mo).padStart(2,'0')}-${String(dy).padStart(2,'0')}`;
  };
  const add = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
  const easter = getEasterDate(year);
  const fixed = [
    { title: 'Nieuwjaarsdag',    date: `${year}-01-01` },
    { title: 'Koningsdag',       date: `${year}-04-27` },
    { title: 'Bevrijdingsdag',   date: `${year}-05-05` },
    { title: 'Eerste Kerstdag',  date: `${year}-12-25` },
    { title: 'Tweede Kerstdag',  date: `${year}-12-26` },
  ];
  const moveable = [
    { title: 'Goede Vrijdag',       date: fmt(add(easter, -2)) },
    { title: 'Eerste Paasdag',      date: fmt(easter)          },
    { title: 'Tweede Paasdag',      date: fmt(add(easter,  1)) },
    { title: 'Hemelvaartsdag',      date: fmt(add(easter, 39)) },
    { title: 'Eerste Pinksterdag',  date: fmt(add(easter, 49)) },
    { title: 'Tweede Pinksterdag',  date: fmt(add(easter, 50)) },
  ];
  return [...fixed, ...moveable].map(h => ({ ...h, type: 'public_holiday', id: 'ph-' + h.date }));
}

function daysSince(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr); d.setHours(0,0,0,0);
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.floor((now - d) / 86400000);
}

function navigateToReportsTab(tab, onNavigate) {
  try { localStorage.setItem('reports_intent_tab', tab); } catch {}
  onNavigate('reports');
}

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function todayStr() { return new Date().toISOString().split('T')[0]; }
function yesterdayStr() {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}
function weekStartStr() {
  const d = new Date(); d.setHours(0,0,0,0);
  const day = d.getDay() === 0 ? 6 : d.getDay() - 1; // Mon=0
  d.setDate(d.getDate() - day);
  return d.toISOString().split('T')[0];
}
function dateInDays(dateStr) {
  const t = new Date(); t.setHours(0,0,0,0);
  const [yr, mo, dy] = dateStr.split('-').map(Number);
  const d = new Date(yr, mo - 1, dy); // local midnight — avoids DST/timezone off-by-one
  return Math.floor((d - t) / 86400000);
}
function fmtEventDate(dateStr) {
  const [yr, mo, dy] = dateStr.split('-').map(Number);
  const d = new Date(yr, mo - 1, dy);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtCurrency(n) {
  if (n == null) return '—';
  return '€' + Math.round(n).toLocaleString('en-GB');
}
function birthdayThisWeek(birthdayStr) {
  if (!birthdayStr) return null;
  // Split string directly — never use new Date(birthdayStr) which parses as UTC
  // and shifts the date back by 1 day in CET/CEST (UTC+1/+2)
  const parts = birthdayStr.split('-');
  const mm = parseInt(parts[1], 10) - 1; // 0-indexed
  const dd = parseInt(parts[2], 10);
  const today = new Date(); today.setHours(0,0,0,0);
  for (const yr of [today.getFullYear(), today.getFullYear() + 1]) {
    const thisYear = new Date(yr, mm, dd); // local midnight, correct day
    const diff = Math.floor((thisYear - today) / 86400000);
    if (diff >= 0 && diff <= 4) return { date: `${yr}-${String(mm+1).padStart(2,'0')}-${String(dd).padStart(2,'0')}`, days: diff };
  }
  return null;
}

// ── Shared components ─────────────────────────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: C.white, border: `1px solid ${C.border}`,
      borderRadius: '12px', overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(44,58,66,0.06)', ...style,
    }}>
      {children}
    </div>
  );
}

function CardHeader({ icon: Icon, title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: `1px solid ${C.border}`, background: C.cream }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        {Icon && <Icon size={13} color={C.navy} strokeWidth={2.2} />}
        <span style={{ fontSize: '11px', fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
      </div>
      {action}
    </div>
  );
}

function AllClear({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '11px 14px' }}>
      <CheckCircle size={13} color={C.green} />
      <span style={{ fontSize: '13px', color: C.mid }}>{label}</span>
    </div>
  );
}

function NavLink({ label, onClick }) {
  return (
    <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: C.skyText, fontWeight: 600, padding: 0 }}>
      {label} <ChevronRight size={11} />
    </button>
  );
}

// ── Shifty Modal ──────────────────────────────────────────────────────────────
const SHIFTY_FIELDS = [
  { key: 'service_notes',     label: 'Service'     },
  { key: 'staff_notes',       label: 'Staff'       },
  { key: 'recognition_notes', label: 'Recognition' },
  { key: 'upselling_notes',   label: 'Upselling'   },
  { key: 'support_notes',     label: 'Support'     },
  { key: 'stock_notes',       label: 'Stock'       },
  { key: 'guest_notes',       label: 'Guests'      },
];

function ShiftyModal({ entry, onClose }) {
  if (!entry) return null;
  const vi = VENUE_IDENTITY[entry.venue];
  const filledFields = SHIFTY_FIELDS.filter(f => entry[f.key]?.trim());
  const time = entry.submitted_at
    ? new Date(entry.submitted_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.55)', zIndex: 800, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px 16px', overflowY: 'auto' }}
      onClick={onClose}>
      <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '500px', boxShadow: '0 24px 64px rgba(0,0,0,0.2)', overflow: 'hidden', marginBottom: '16px' }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: C.navy, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '15px', color: C.white }}>
              {entry.manager_name || 'Shift Report'}{entry.shift ? ` · ${entry.shift}` : ''}
            </div>
            <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>
              {entry.venue}{time ? ` · ${time}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>
        {/* Body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filledFields.length === 0 ? (
            <p style={{ color: C.mid, fontSize: '13px', margin: 0 }}>No notes were filled in for this shift.</p>
          ) : (
            filledFields.map(f => (
              <div key={f.key}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{f.label}</div>
                <div style={{ fontSize: '13px', color: C.charcoal, lineHeight: 1.6, background: C.cream, borderRadius: '8px', padding: '9px 12px' }}>
                  {entry[f.key]}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Shifty Yesterday ──────────────────────────────────────────────────────────
function ShiftyYesterday({ venues }) {
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(false);
  const [modal,    setModal]    = useState(null);
  const yesterday = yesterdayStr();
  const dayLabel  = new Date(yesterday + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });

  useEffect(() => {
    async function load() {
      let q = supabase.from('shift_log')
        .select('id,venue,shift,manager_name,submitted_at,service_notes,staff_notes,recognition_notes,upselling_notes,support_notes,stock_notes,guest_notes')
        .gte('submitted_at', yesterday + 'T00:00:00')
        .lte('submitted_at', yesterday + 'T23:59:59')
        .order('submitted_at', { ascending: false });
      if (venues?.length) q = q.in('venue', venues);
      const { data, error } = await q;
      if (error) setError(true);
      else setEntries(data || []);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  if (error) return null;

  return (
    <>
      {modal && <ShiftyModal entry={modal} onClose={() => setModal(null)} />}
      <Card>
        <CardHeader icon={MessageSquare} title={`Shifty · ${dayLabel}`} />
        {loading ? (
          <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
        ) : entries.length === 0 ? (
          <AllClear label="No shift logs submitted yesterday" />
        ) : (
          <div style={{ padding: '4px 0' }}>
            {entries.map((e, i) => {
              const vi = VENUE_IDENTITY[e.venue];
              const preview = [e.service_notes, e.staff_notes, e.stock_notes].find(n => n?.trim()) || '';
              return (
                <button key={e.id} onClick={() => setModal(e)}
                  style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 14px', borderBottom: i < entries.length - 1 ? `1px solid ${C.border}` : 'none', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderRadius: 0 }}
                  onMouseEnter={e => e.currentTarget.style.background = C.cream}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>
                      {e.manager_name || 'Unknown'}{e.shift ? ` · ${e.shift}` : ''}
                    </div>
                    {preview && (
                      <div style={{ fontSize: '12px', color: C.mid, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preview.slice(0, 80)}{preview.length > 80 ? '…' : ''}
                      </div>
                    )}
                  </div>
                  {vi && <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0, marginTop: '2px' }}>{vi.short}</span>}
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}

// ── MTD Cards ─────────────────────────────────────────────────────────────────
function MTDCards({ venues, isMobile, onNavigate }) {
  const [data,     setData]     = useState({});
  const [loading,  setLoading]  = useState(true);
  const [dayCount, setDayCount] = useState(null); // { reported, total }
  const now       = new Date();
  const year      = now.getFullYear();
  const month     = now.getMonth() + 1;
  const monthName = MONTHS_SHORT[now.getMonth()];

  useEffect(() => {
    async function load() {
      const targetVenues = venues.filter(v => v !== 'Prep Kitchen');
      if (!targetVenues.length) { setLoading(false); return; }

      const monthStr   = `${year}-${String(month).padStart(2,'0')}`;
      const lyMonthStr = `${year-1}-${String(month).padStart(2,'0')}`;
      // First day of next month — handles December rollover for both CY and LY
      const nextMonthStr   = month === 12
        ? `${year+1}-01-01`
        : `${year}-${String(month+1).padStart(2,'0')}-01`;
      const lyNextMonthStr = month === 12
        ? `${year}-01-01`
        : `${year-1}-${String(month+1).padStart(2,'0')}-01`;
      const [cyMonthly, lyMonthly, lyDailyRows, cyDailyRows, reviewsRes] = await Promise.all([
        Promise.all(targetVenues.map(v =>
          supabase.from('results_monthly')
            .select('actual_covers,budget_revenue,budget_covers,kitchen_hours,foh_hours')
            .eq('venue', v).eq('year', year).eq('month', month).maybeSingle()
        )),
        Promise.all(targetVenues.map(v =>
          supabase.from('results_monthly')
            .select('actual_revenue,actual_covers,kitchen_hours,foh_hours')
            .eq('venue', v).eq('year', year - 1).eq('month', month).maybeSingle()
        )),
        Promise.all(targetVenues.map(v =>
          supabase.from('results_daily').select('date,total_revenue')
            .eq('venue', v)
            .gte('date', `${lyMonthStr}-01`)
            .lt('date', lyNextMonthStr)
        )),
        Promise.all(targetVenues.map(v =>
          supabase.from('results_daily').select('date,total_revenue')
            .eq('venue', v)
            .gte('date', `${monthStr}-01`)
            .lt('date', nextMonthStr)
        )),
        supabase.from('google_reviews')
          .select('venue,star_rating,review_date')
          .in('venue', targetVenues)
          .gte('review_date', `${lyMonthStr}-01`)
          .lt('review_date', nextMonthStr),
      ]);

      const totalDaysInMonth = new Date(year, month, 0).getDate();
      const monthStart       = new Date(year, month - 1, 1);
      const result = {};

      targetVenues.forEach((v, i) => {
        const cy      = cyMonthly[i].data;
        const ly      = lyMonthly[i].data;
        const cyDaily = (cyDailyRows[i].data || []).filter(d => (d.total_revenue||0) > 0);
        const lyDaily = lyDailyRows[i].data || [];

        // MTD revenue = sum of daily rows with revenue > 0
        const mtdRev = cyDaily.reduce((s, d) => s + (d.total_revenue||0), 0) || null;

        // Most recent day with revenue — anchor for LY comparison
        const latestDateStr = cyDaily.length > 0 ? cyDaily.map(d => d.date).sort().reverse()[0] : null;
        const latestDate    = latestDateStr ? new Date(latestDateStr) : null;
        const reportedDays  = latestDate ? Math.round((latestDate - monthStart) / 86400000) + 1 : totalDaysInMonth;
        const dayOfMonth    = latestDate ? latestDate.getDate() : null;

        if (i === 0) setDayCount({ reported: reportedDays, total: totalDaysInMonth });

        // LY MTD = sum daily up to same day of month (same logic as GroupResults)
        const lyMtdRev = dayOfMonth
          ? lyDaily.filter(d => new Date(d.date).getMonth()+1 === month && new Date(d.date).getDate() <= dayOfMonth && (d.total_revenue||0) > 0)
              .reduce((s, d) => s + (d.total_revenue||0), 0) || null
          : null;

        // Productivity: Rev/Hr — MTD revenue / full-month hours from results_monthly
        // LY uses full-month revenue / full-month hours (KPI reference)
        const cyHours   = (cy?.kitchen_hours || 0) + (cy?.foh_hours || 0);
        const lyHours   = (ly?.kitchen_hours || 0) + (ly?.foh_hours || 0);
        const cyRevPerHr = cyHours > 0 && mtdRev ? Math.round(mtdRev / cyHours) : null;
        const lyRevPerHr = lyHours > 0 && ly?.actual_revenue ? Math.round(ly.actual_revenue / lyHours) : null;

        const venueReviews = (reviewsRes.data || []).filter(r =>
          r.venue === v && r.review_date >= `${monthStr}-01` && r.review_date < nextMonthStr
        );
        const fiveStarMtd  = venueReviews.filter(r => r.star_rating === 5).length;
        const mtdAvgRating = venueReviews.length > 0
          ? (Math.floor(venueReviews.reduce((s, r) => s + r.star_rating, 0) / venueReviews.length * 10) / 10).toFixed(1)
          : null;

        // LY MTD 5★ — same day-of-month cap
        const todayDay = new Date().getDate();
        const lyFive   = (reviewsRes.data || []).filter(r =>
          r.venue === v && r.star_rating === 5 &&
          r.review_date >= `${lyMonthStr}-01` && r.review_date < lyNextMonthStr &&
          new Date(r.review_date).getDate() <= todayDay
        ).length;

        result[v] = {
          rev:          mtdRev,
          lyRev:        lyMtdRev,
          revDiff:      mtdRev != null && lyMtdRev != null ? mtdRev - lyMtdRev : null,
          revPerHr:     cyRevPerHr,
          hrDiff:       cyRevPerHr != null && lyRevPerHr != null ? cyRevPerHr - lyRevPerHr : null,
          fiveStarMtd:  venueReviews.length > 0 ? fiveStarMtd : null,
          mtdTotal:     venueReviews.length > 0 ? venueReviews.length : null,
          fiveStarLY:   lyFive > 0 ? lyFive : null,
          mtdAvgRating,
        };
      });

      setData(result);
      setLoading(false);
    }
    load();
  }, [venues.join(','), year, month]);

  const targetVenues = venues.filter(v => v !== 'Prep Kitchen');
  if (!targetVenues.length) return null;

  function handleVenueClick(venue) {
    // Store intent in localStorage — Results.js reads on mount
    try {
      localStorage.setItem('results_intent_venue', venue);
      localStorage.setItem('results_intent_tab', 'weekly');
    } catch {}
    onNavigate('results');
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingLeft: '2px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {monthName} MTD
        </span>
        {dayCount && (
          <span style={{ fontSize: '11px', fontWeight: 600, color: C.mid }}>
            · {dayCount.reported}/{dayCount.total} days
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(targetVenues.length, 3)}, 1fr)`, gap: '8px' }}>
        {targetVenues.map(venue => {
          const vi   = VENUE_IDENTITY[venue] || {};
          const d    = data[venue] || {};
          const isUp  = d.revDiff  != null ? d.revDiff  >= 0 : null;
          const hrUp  = d.hrDiff   != null ? d.hrDiff   >= 0 : null;
          return (
            <div key={venue}
              onClick={() => handleVenueClick(venue)}
              style={{ background: C.white, border: `1.5px solid ${vi.border || C.border}`, borderRadius: '10px', padding: '12px 10px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
              <div style={{ fontSize: '10px', fontWeight: 800, color: vi.text, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px' }}>
                {vi.short}
              </div>
              {loading ? (
                <div style={{ height: '28px', background: C.cream, borderRadius: '6px', marginBottom: '6px' }} />
              ) : d.rev != null ? (
                <>
                  <div style={{ fontSize: isMobile ? '16px' : '19px', fontWeight: 800, color: C.charcoal, lineHeight: 1.1, marginBottom: '5px' }}>
                    {fmtCurrency(d.rev)}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: C.mid, marginBottom: '2px' }}>VS LY</div>
                  {d.revDiff != null ? (
                    <div style={{ fontSize: '12px', fontWeight: 700, color: isUp ? C.green : C.red, marginBottom: '6px' }}>
                      {isUp ? '▲' : '▼'} {fmtCurrency(Math.abs(d.revDiff))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '11px', color: C.mid, marginBottom: '6px' }}>No LY data</div>
                  )}
                  {d.revPerHr != null && (
                    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '5px' }}>
                      <div style={{ fontSize: '10px', color: C.mid, fontWeight: 600 }}>PRODUCTIVITY</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: C.charcoal }}>€{d.revPerHr}/hr</span>
                        {d.hrDiff != null && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: hrUp ? C.green : C.red }}>
                            {hrUp ? '+' : ''}€{d.hrDiff}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {(d.mtdAvgRating != null || d.fiveStarMtd != null) && (
                    <div style={{ marginTop: '6px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Reviews</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {d.fiveStarMtd != null && (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: C.charcoal, lineHeight: 1 }}>{d.fiveStarMtd}</span>
                            <span style={{ fontSize: '8px', fontWeight: 700, color: C.mid, lineHeight: 1, marginTop: '1px', display: 'flex', alignItems: 'center', gap: '1px' }}>
                              5<Star size={6} fill='#F7EC71' stroke='#E4AC49' strokeWidth={1.5} />
                            </span>
                          </div>
                        )}
                        <span style={{ width: '1px', height: '14px', background: C.border, flexShrink: 0 }} />
                        {d.mtdAvgRating != null && (
                          <span style={{ fontSize: '14px', fontWeight: 800, color: C.charcoal, lineHeight: 1 }}>
                            {d.mtdAvgRating}<span style={{ fontSize: '10px', fontWeight: 500, color: C.mid, marginLeft: '2px' }}>AVG</span>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: '12px', color: C.mid, paddingTop: '4px' }}>No data yet</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Upcoming Events ───────────────────────────────────────────────────────────
function UpcomingEvents({ venues, teamMembers, onNavigate }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const from = todayStr();
      const to   = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; })();
      const { data } = await supabase.from('calendar_events')
        .select('id,title,date,type,venue').gte('date', from).lte('date', to).order('date');
      setEvents(data || []);
      setLoading(false);
    }
    load();
  }, []);

  const birthdays = (teamMembers || []).filter(m => m.active !== false).flatMap(m => {
    const bd = birthdayThisWeek(m.birthday);
    if (!bd) return [];
    if (venues && !venues.includes('All Venues') && !venues.includes(m.venue)) return [];
    return [{ id: `bd-${m.id}`, title: `🎂 ${m.name}`, date: bd.date, type: 'birthday', venue: m.venue }];
  });

  const calFiltered = events.filter(ev => {
    if (ev.venue === 'All Venues') return true;
    if (!venues || venues.includes('All Venues')) return true;
    return venues.includes(ev.venue);
  });

  // Public holidays — computed client-side (not in Supabase)
  const today7 = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; })();
  const from = todayStr();
  const thisYr = new Date().getFullYear();
  const publicHolidays = [
    ...getNLHolidays(thisYr),
    ...getNLHolidays(thisYr + 1),
  ].filter(h => h.date >= from && h.date <= today7);

  const all = [...calFiltered, ...birthdays, ...publicHolidays].sort((a, b) => a.date.localeCompare(b.date));

  const chip = (type, venue) => {
    if (type === 'birthday')       return { color: C.pinkText, bg: C.pinkLight,   label: 'Birthday'     };
    if (type === 'public_holiday') return { color: '#6B8494',  bg: '#DDE6EC',     label: 'Holiday'      };
    if (type === 'contract_end')   return { color: C.red,      bg: C.redLight,    label: 'Contract End' };
    if (type === 'probation_end')  return { color: C.amber,    bg: C.amberLight,  label: 'Probation'    };
    const vi = VENUE_IDENTITY[venue];
    return vi ? { color: vi.text, bg: vi.bg, label: vi.short }
              : { color: C.navy,  bg: C.skyBlueLight, label: 'Event' };
  };

  return (
    <Card>
      <CardHeader icon={Calendar} title="Upcoming · Next 7 Days"
        action={<NavLink label="Calendar" onClick={() => onNavigate('calendar')} />} />
      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : all.length === 0 ? (
        <AllClear label="Nothing scheduled in the next 7 days" />
      ) : (
        <div style={{ padding: '4px 0' }}>
          {all.map((ev, i) => {
            const days     = dateInDays(ev.date);
            const c        = chip(ev.type, ev.venue);
            const dayLabel = days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : fmtEventDate(ev.date);
            return (
              <div key={ev.id + i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: i < all.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ minWidth: '70px', flexShrink: 0 }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: days <= 1 ? C.amber : C.mid }}>{dayLabel}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                  {ev.venue && ev.venue !== 'All Venues' && (
                    <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px' }}>{ev.venue}</div>
                  )}
                </div>
                <span style={{ fontSize: '10px', fontWeight: 700, color: c.color, background: c.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>
                  {c.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Checklist Status — FOH missed shifts only ────────────────────────────────
// ── Reviews Card ──────────────────────────────────────────────────────────────
function ReviewsCard({ venues, onNavigate }) {
  const [latest,  setLatest]  = useState([]);
  const [loading, setLoading] = useState(true);

  const targetVenues = venues.filter(v => v !== 'Prep Kitchen');

  useEffect(() => {
    async function load() {
      if (!targetVenues.length) { setLoading(false); return; }
      // Fetch latest review per venue
      const results = await Promise.all(
        targetVenues.map(v =>
          supabase.from('google_reviews')
            .select('id,venue,reviewer_name,star_rating,review_date,comment')
            .eq('venue', v)
            .order('review_date', { ascending: false })
            .limit(1)
            .maybeSingle()
        )
      );
      setLatest(results.map(r => r.data).filter(Boolean));
      setLoading(false);
    }
    load();
  }, [targetVenues.join(',')]);

  if (!targetVenues.length) return null;

  return (
    <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, padding: '14px', marginBottom: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 800, color: C.charcoal }}>Recent Reviews</span>
        </div>
        <button
          onClick={() => onNavigate('reviews')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: C.skyText, padding: 0 }}
        >
          View all →
        </button>
      </div>

      {loading ? (
        <div style={{ height: '60px', background: C.cream, borderRadius: '8px' }} />
      ) : latest.length === 0 ? (
        <div style={{ fontSize: '12px', color: C.mid }}>No reviews synced yet</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {latest.map(r => {
            const vi = VENUE_IDENTITY[r.venue] || {};
            return (
              <div key={r.id} style={{ background: C.cream, borderRadius: '8px', padding: '9px 11px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: vi.bg, color: vi.text, border: `1px solid ${vi.border}` }}>
                    {vi.short}
                  </span>
                  <div style={{ display: 'flex', gap: '1px' }}>
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={10} fill={i <= r.star_rating ? '#F7EC71' : 'none'} stroke={i <= r.star_rating ? '#E4AC49' : C.border} strokeWidth={1.5} />
                    ))}
                  </div>
                  <span style={{ fontSize: '10px', color: C.mid }}>{r.reviewer_name || 'Anonymous'}</span>
                </div>
                {r.comment && (
                  <div style={{ fontSize: '11px', color: C.charcoal, lineHeight: 1.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {r.comment.trim()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// FOH daily checklists exist at Jordaan and Plantage only
const FOH_CHECKLIST_VENUES = ['Box Jordaan', 'Box Plantage'];

function ChecklistStatus({ venues, onNavigate }) {
  const [missed,  setMissed]  = useState([]);
  const [loading, setLoading] = useState(true);
  const yesterday = yesterdayStr();

  useEffect(() => {
    async function load() {
      const fohVenues = (venues || []).filter(v => FOH_CHECKLIST_VENUES.includes(v));
      if (!fohVenues.length) { setLoading(false); return; }

      const { data } = await supabase.from('checklist_log')
        .select('venue,shift,department')
        .eq('date', yesterday)
        .in('venue', fohVenues);

      const missedList = [];
      fohVenues.forEach(venue => {
        const shifts = ['opening', ...(!NO_MIDSHIFT_VENUES.includes(venue) ? ['mid-shift'] : []), 'closing'];
        const venueEntries = (data || []).filter(e => e.venue === venue);
        const missedShifts = shifts.filter(shift =>
          !venueEntries.some(e => e.shift === shift || e.shift?.startsWith(shift))
        );
        if (missedShifts.length > 0) missedList.push({ venue, missedShifts });
      });

      setMissed(missedList);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  const fohVenues = (venues || []).filter(v => FOH_CHECKLIST_VENUES.includes(v));
  if (!fohVenues.length) return null;

  return (
    <Card>
      <CardHeader icon={CheckCircle} title="Checklists · Yesterday"
        action={onNavigate && <NavLink label="Reports" onClick={() => navigateToReportsTab('checklists', onNavigate)} />} />
      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : missed.length === 0 ? (
        <AllClear label="All checklists completed yesterday" />
      ) : (
        <div style={{ padding: '4px 0' }}>
          {missed.map(({ venue, missedShifts }, i) => {
            const vi = VENUE_IDENTITY[venue];
            return (
              <div key={venue} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', borderBottom: i < missed.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.charcoal, minWidth: '72px', flexShrink: 0 }}>
                  {vi?.short || venue}
                </span>
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', flex: 1 }}>
                  {missedShifts.map(s => (
                    <span key={s} style={{ fontSize: '11px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 8px', borderRadius: '20px' }}>
                      ✗ {s === 'opening' ? 'Opening' : s === 'mid-shift' ? 'Mid-Shift' : 'Closing'}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Maintenance Widget ────────────────────────────────────────────────────────
const MAINT_STATUS_CFG = {
  open:        { label: 'Open',        color: '#E07B00', bg: '#FFF3E0'   },
  in_progress: { label: 'In Progress', color: '#3A8FA3', bg: '#EAF4F8'  },
  resolved:    { label: 'Resolved',    color: '#7EA594', bg: '#EAF2EF'  },
};

const MAINT_PRIORITY_CFG = {
  normal: { label: 'Normal', color: '#445366', bg: '#eef1f5' },
  urgent: { label: 'Urgent', color: '#DC3545', bg: '#FFF0F0' },
};

const MAINT_CATEGORIES = [
  { key: 'Equipment', color: '#3A8FA3', bg: '#EAF4F8' },
  { key: 'Building',  color: '#E07B00', bg: '#FFF3E0' },
  { key: 'Safety',    color: '#DC3545', bg: '#FFF0F0' },
  { key: 'Other',     color: '#7A8A96', bg: '#F7F6EF' },
];

function MaintBadge({ label, color, bg }) {
  return (
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, color, background: bg,
      padding: '0.1rem 0.45rem', borderRadius: '999px',
      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function HomepageTaskModal({ task, currentUser, onClose, onUpdate }) {
  const [saving,      setSaving]      = useState(false);
  const [resNote,     setResNote]     = useState('');
  const [showResNote, setShowResNote] = useState(false);

  async function changeStatus(status) {
    setSaving(true);
    const patch = { status };
    if (status === 'resolved') {
      patch.resolved_by = currentUser?.name || 'unknown';
      patch.resolved_at = new Date().toISOString();
      patch.resolution_notes = resNote.trim() || null;
    } else {
      patch.resolved_by = null;
      patch.resolved_at = null;
    }
    const { data } = await supabase.from('maintenance_tasks')
      .update(patch).eq('id', task.id).select().single();
    if (data) onUpdate(data);
    setSaving(false);
    if (status === 'resolved') onClose();
  }

  const catCfg    = MAINT_CATEGORIES.find(c => c.key === task.category) || MAINT_CATEGORIES[MAINT_CATEGORIES.length - 1];
  const statusCfg = MAINT_STATUS_CFG[task.status] || MAINT_STATUS_CFG.open;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(44,58,66,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: C.white, width: '100%', maxWidth: '520px', borderRadius: '16px 16px 0 0', padding: '1.25rem 1.5rem 2rem', boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ width: '36px', height: '4px', background: C.border, borderRadius: '2px', margin: '0 auto 1rem' }} />
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
          <MaintBadge label={task.priority === 'urgent' ? '⚠ Urgent' : 'Normal'} color={MAINT_PRIORITY_CFG[task.priority]?.color || C.mid} bg={MAINT_PRIORITY_CFG[task.priority]?.bg || C.cream} />
          <MaintBadge label={statusCfg.label} color={statusCfg.color} bg={statusCfg.bg} />
          <MaintBadge label={task.category} color={catCfg.color} bg={catCfg.bg} />
        </div>
        <p style={{ margin: '0 0 0.2rem', fontWeight: 700, fontSize: '0.95rem', color: C.charcoal }}>{task.title}</p>
        {task.equipment_name && (
          <p style={{ margin: '0 0 0.2rem', fontSize: '0.75rem', color: C.skyText, fontWeight: 600 }}>🔧 {task.equipment_name}</p>
        )}
        <p style={{ margin: '0 0 1rem', fontSize: '0.72rem', color: C.mid }}>{task.venue} · {task.area || 'General'}</p>

        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {task.status !== 'open' && (
            <button onClick={() => changeStatus('open')} disabled={saving}
              style={{ padding: '0.4rem 0.85rem', borderRadius: '8px', border: `1px solid ${C.border}`, background: C.white, color: C.mid, fontSize: '0.8rem', cursor: 'pointer' }}>
              Reopen
            </button>
          )}
          {task.status === 'open' && (
            <button onClick={() => changeStatus('in_progress')} disabled={saving}
              style={{ padding: '0.4rem 0.85rem', borderRadius: '8px', border: `1px solid ${C.skyText}44`, background: C.skyBlueLight, color: C.skyText, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
              Mark in progress
            </button>
          )}
          {task.status !== 'resolved' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: 1 }}>
              <button onClick={() => setShowResNote(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.85rem', borderRadius: '8px', border: `1px solid ${C.green}44`, background: C.greenLight, color: C.green, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
                <CheckCircle size={13} /> Mark resolved <ChevronRight size={12} style={{ transform: showResNote ? 'rotate(90deg)' : 'none' }} />
              </button>
              {showResNote && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <textarea value={resNote} onChange={e => setResNote(e.target.value)}
                    placeholder="Resolution notes (optional)..." rows={2}
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '0.82rem', resize: 'vertical', boxSizing: 'border-box', color: C.charcoal }} />
                  <button onClick={() => changeStatus('resolved')} disabled={saving}
                    style={{ padding: '0.4rem 1rem', background: C.green, color: C.white, border: 'none', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
                    {saving ? 'Saving…' : 'Confirm resolved'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose}
          style={{ marginTop: '1rem', width: '100%', padding: '0.45rem', background: C.cream, border: 'none', borderRadius: '8px', color: C.mid, fontSize: '0.82rem', cursor: 'pointer' }}>
          Close
        </button>
      </div>
    </div>
  );
}

function MaintenanceWidget({ venues, currentUser, onNavigate }) {
  const [urgentTasks,  setUrgentTasks]  = useState([]);
  const [recentTasks,  setRecentTasks]  = useState([]);
  const [serviceDue,   setServiceDue]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);

  const isManager = ['admin', 'manager'].includes(currentUser?.role);
  const canEdit   = isManager;

  useEffect(() => {
    async function load() {
      let urgentQ = supabase.from('maintenance_tasks')
        .select('id,title,venue,area,category,priority,status,equipment_name,reported_at')
        .eq('priority', 'urgent').neq('status', 'resolved')
        .order('reported_at', { ascending: false });
      if (venues?.length) urgentQ = urgentQ.in('venue', venues);

      let recentQ = supabase.from('maintenance_tasks')
        .select('id,title,venue,area,category,priority,status,equipment_name,reported_at')
        .neq('status', 'resolved')
        .order('reported_at', { ascending: false })
        .limit(5);
      if (venues?.length) recentQ = recentQ.in('venue', venues);

      let svcQ = supabase.from('maintenance_equipment')
        .select('id,name,venue,last_serviced_date,service_interval_days')
        .eq('has_service_contract', true).eq('active', true);
      if (venues?.length) svcQ = svcQ.in('venue', venues);

      const [{ data: urgent }, { data: recent }, { data: equip }] = await Promise.all([
        urgentQ, recentQ, svcQ,
      ]);

      setUrgentTasks(urgent || []);

      const urgentIds = new Set((urgent || []).map(t => t.id));
      setRecentTasks((recent || []).filter(t => !urgentIds.has(t.id)).slice(0, 3));

      const due = (equip || []).filter(e => {
        if (!e.last_serviced_date || !e.service_interval_days) return false;
        const next = new Date(e.last_serviced_date);
        next.setDate(next.getDate() + e.service_interval_days);
        return Math.ceil((next - Date.now()) / 86400000) <= 14;
      });
      setServiceDue(due);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  function handleUpdate(updated) {
    setUrgentTasks(prev => updated.status === 'resolved'
      ? prev.filter(t => t.id !== updated.id)
      : prev.map(t => t.id === updated.id ? updated : t)
    );
    setRecentTasks(prev => updated.status === 'resolved'
      ? prev.filter(t => t.id !== updated.id)
      : prev.map(t => t.id === updated.id ? updated : t)
    );
    setSelectedTask(null);
  }

  function TaskRow({ task }) {
    const catCfg    = MAINT_CATEGORIES.find(c => c.key === task.category) || MAINT_CATEGORIES[MAINT_CATEGORIES.length - 1];
    const statusCfg = MAINT_STATUS_CFG[task.status] || MAINT_STATUS_CFG.open;
    const vi        = VENUE_IDENTITY[task.venue];
    const age       = Math.floor((Date.now() - new Date(task.reported_at)) / 86400000);
    return (
      <button
        onClick={() => canEdit ? setSelectedTask(task) : onNavigate('maintenance')}
        style={{
          width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px',
          padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
          textAlign: 'left',
          borderLeft: `3px solid ${task.priority === 'urgent' ? C.red : task.status === 'in_progress' ? C.skyText : C.border}`,
          marginLeft: '-1px',
        }}
        onMouseEnter={e => e.currentTarget.style.background = C.cream}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        {task.priority === 'urgent' && (
          <AlertTriangle size={13} color={C.red} style={{ flexShrink: 0, marginTop: '2px' }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {task.title}
          </div>
          <div style={{ display: 'flex', gap: '5px', marginTop: '2px', flexWrap: 'wrap', alignItems: 'center' }}>
            <MaintBadge label={statusCfg.label} color={statusCfg.color} bg={statusCfg.bg} />
            <MaintBadge label={task.category} color={catCfg.color} bg={catCfg.bg} />
            {task.equipment_name && (
              <span style={{ fontSize: '10px', color: C.skyText, fontWeight: 600 }}>🔧 {task.equipment_name}</span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>
            {task.venue} · {age === 0 ? 'today' : `${age}d ago`}
          </div>
        </div>
        {vi && (
          <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0, marginTop: '2px' }}>
            {vi.short}
          </span>
        )}
      </button>
    );
  }

  const hasAnything = urgentTasks.length > 0 || recentTasks.length > 0 || serviceDue.length > 0;

  return (
    <>
      {selectedTask && canEdit && (
        <HomepageTaskModal
          task={selectedTask}
          currentUser={currentUser}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleUpdate}
        />
      )}
      <Card>
        <CardHeader
          icon={Wrench}
          title="Maintenance"
          action={<NavLink label="View all" onClick={() => onNavigate('maintenance')} />}
        />
        {loading ? (
          <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
        ) : !hasAnything ? (
          <AllClear label="No open maintenance tasks" />
        ) : (
          <div style={{ padding: '4px 0' }}>
            {/* Service due alerts */}
            {serviceDue.length > 0 && (
              <div style={{ padding: '8px 14px', borderBottom: `1px solid ${C.border}` }}>
                {serviceDue.map((e, i) => {
                  const next = new Date(e.last_serviced_date);
                  next.setDate(next.getDate() + e.service_interval_days);
                  const daysLeft = Math.ceil((next - Date.now()) / 86400000);
                  const overdue  = daysLeft < 0;
                  const vi       = VENUE_IDENTITY[e.venue];
                  return (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < serviceDue.length - 1 ? '5px' : 0 }}>
                      <RefreshCw size={11} color={overdue ? C.red : C.amber} style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '11px', color: overdue ? C.red : C.amber, fontWeight: 600, flex: 1 }}>
                        {e.name} — {overdue ? `service overdue ${Math.abs(daysLeft)}d` : `service due in ${daysLeft}d`}
                      </span>
                      {vi && <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>{vi.short}</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Urgent tasks */}
            {urgentTasks.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    ⚠ Urgent ({urgentTasks.length})
                  </span>
                </div>
                {urgentTasks.map((task, i) => (
                  <div key={task.id} style={{ borderBottom: (i < urgentTasks.length - 1 || recentTasks.length > 0) ? `1px solid ${C.border}` : 'none' }}>
                    <TaskRow task={task} />
                  </div>
                ))}
              </>
            )}
            {/* Recently logged */}
            {recentTasks.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px' }}>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Recent
                  </span>
                </div>
                {recentTasks.map((task, i) => (
                  <div key={task.id} style={{ borderBottom: i < recentTasks.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <TaskRow task={task} />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

// ── Fridge Temp Alert ─────────────────────────────────────────────────────────
function FridgeTempAlert({ venues }) {
  const [alerts,  setAlerts]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const from = new Date(); from.setDate(from.getDate() - 5);
      const { data } = await supabase.from('fridge_temp_logs')
        .select('fridge_id,temp,logged_at,fridges(name,type,venue,max_temp,min_temp)')
        .gte('logged_at', from.toISOString())
        .order('logged_at', { ascending: false });
      if (!data) { setLoading(false); return; }
      const filtered = (data || []).filter(log => !venues?.length || venues.includes(log.fridges?.venue));
      const byFridge = {};
      filtered.forEach(log => {
        if (!byFridge[log.fridge_id]) byFridge[log.fridge_id] = { fridge: log.fridges, logs: [] };
        byFridge[log.fridge_id].logs.push(log);
      });
      const flagged = [];
      Object.values(byFridge).forEach(({ fridge, logs }) => {
        if (!fridge?.max_temp) return;
        let consecutive = 0;
        for (const log of logs) { if (log.temp > fridge.max_temp) consecutive++; else break; }
        if (consecutive >= 3) flagged.push({ name: fridge.name, venue: fridge.venue, days: consecutive });
      });
      setAlerts(flagged);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  return (
    <Card>
      <CardHeader icon={Thermometer} title="Fridge Temp Alerts" />
      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : alerts.length === 0 ? (
        <AllClear label="All fridge temps within range" />
      ) : (
        <div style={{ padding: '4px 0' }}>
          {alerts.map((a, i) => {
            const vi = VENUE_IDENTITY[a.venue];
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', borderBottom: i < alerts.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <Thermometer size={13} color={C.red} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>{a.name}</div>
                  <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px' }}>{a.venue} · {a.days} consecutive red readings</div>
                </div>
                {vi && <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>{vi.short}</span>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Contracts Ending ──────────────────────────────────────────────────────────
function ContractsEnding({ venues, onNavigate }) {
  const [count,   setCount]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const in42 = new Date(); in42.setDate(in42.getDate() + 42);
      let q = supabase.from('team_members')
        .select('id', { count: 'exact', head: true })
        .eq('contract_type', 'Fixed Term').eq('active', true)
        .gte('contract_end_date', todayStr())
        .lte('contract_end_date', in42.toISOString().split('T')[0]);
      if (venues?.length) q = q.in('venue', venues);
      const { count: n } = await q;
      setCount(n || 0);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  return (
    <Card>
      <CardHeader icon={Clock} title="Contracts Ending Soon"
        action={<NavLink label="Team" onClick={() => onNavigate('team')} />} />
      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : count === 0 ? (
        <AllClear label="No contracts ending in the next 6 weeks" />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '24px', fontWeight: 800, color: C.amber }}>{count}</span>
            <span style={{ fontSize: '13px', color: C.charcoal }}>{count === 1 ? 'contract' : 'contracts'} ending within 6 weeks</span>
          </div>
          <button onClick={() => onNavigate('team')} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: C.amberLight, border: 'none', borderRadius: '7px', padding: '6px 10px', cursor: 'pointer', color: C.amber, fontSize: '12px', fontWeight: 700 }}>
            Review <ChevronRight size={12} />
          </button>
        </div>
      )}
    </Card>
  );
}

// ── New Staff Onboarding — expandable card ───────────────────────────────────
function DeptIcon({ dept }) {
  const map = {
    FOH: { bg: C.skyBlueLight, color: C.skyText, Icon: Coffee },
    KIT: { bg: C.orangeLight,  color: C.amber,   Icon: ChefHat },
  };
  const cfg = map[dept] || { bg: C.cream, color: C.mid, Icon: Users };
  const { bg, color, Icon } = cfg;
  return (
    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={14} color={color} strokeWidth={1.8} />
    </div>
  );
}

function NewStaffOnboarding({ venues, onNavigate }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      let q = supabase.from('team_members')
        .select('id,name,venue,dept,join_date,contract_start_date')
        .eq('active', true)
        .or('probation_done.eq.false,probation_done.is.null');
      if (venues?.length) q = q.in('venue', venues);
      const { data } = await q;
      setMembers(data || []);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  // Per-venue summary
  const venueList = venues?.length ? venues : [...new Set(members.map(m => m.venue))];
  const byVenue = {};
  members.forEach(m => {
    if (!byVenue[m.venue]) byVenue[m.venue] = { depts: {}, overdue: 0 };
    const d = m.dept || 'Other';
    byVenue[m.venue].depts[d] = (byVenue[m.venue].depts[d] || 0) + 1;
    const days = Math.floor((Date.now() - new Date(m.join_date || m.contract_start_date)) / 86400000);
    if (days > 28) byVenue[m.venue].overdue += 1;
  });

  const DEPT_ABBR = { 'Management': 'Mgmt' };

  return (
    <Card>
      <CardHeader icon={Users} title="Onboarding"
        action={<NavLink label="Team" onClick={() => onNavigate('team')} />} />

      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : (
        <div style={{ padding: '4px 0' }}>
          {venueList.map((venue, i) => {
            const vi     = VENUE_IDENTITY[venue] || {};
            const info   = byVenue[venue];
            const isLast = i === venueList.length - 1;

            if (!info) {
              return (
                <div key={venue}
                  onClick={() => onNavigate('team')}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = C.cream}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={13} color={C.green} />
                    <span style={{ fontSize: '13px', color: C.mid }}>All good</span>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px' }}>
                    {vi.short || venue}
                  </span>
                </div>
              );
            }

            const deptParts = Object.entries(info.depts).map(([d, n], idx, arr) => (
              <span key={d} style={{ display: 'inline-flex', alignItems: 'baseline', gap: '3px' }}>
                <span style={{ fontSize: '15px', fontWeight: 800, color: C.purple }}>{n}</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: C.mid }}>{DEPT_ABBR[d] || d}</span>
                {idx < arr.length - 1 && <span style={{ fontSize: '12px', color: C.border, margin: '0 3px' }}>·</span>}
              </span>
            ));

            return (
              <div key={venue}
                onClick={() => onNavigate('team')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '9px 14px', borderBottom: isLast ? 'none' : `1px solid ${C.border}`, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = C.cream}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    {deptParts}
                    {info.overdue > 0 && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 7px', borderRadius: '20px', marginLeft: '4px' }}>
                        {info.overdue} overdue
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>incomplete onboarding</div>
                </div>
                <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>
                  {vi.short || venue}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── Leave Requests Card (supervisor+) ────────────────────────────────────────
const EITJE_ENV_MAP_HP = { 3080:'Box Jordaan', 3081:'Box Plantage', 5537:'Sassys', 3660:'Prep Kitchen' };
const BASE_URL_HP = 'https://open-api.eitje.app/open_api';

function LeaveRequestsCard({ onNavigate }) {
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(false);
  const [expanded,  setExpanded]  = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const { data: settings } = await supabase.from('app_settings').select('key,value')
          .in('key', ['eitje_partner_username','eitje_partner_password','eitje_api_username','eitje_api_password']);
        if (!settings || settings.length < 4) { setError(true); setLoading(false); return; }
        const c = {};
        settings.forEach(r => { c[r.key] = r.value; });
        const h = {
          'Content-Type': 'application/json',
          'Partner-Username': c.eitje_partner_username,
          'Partner-Password': c.eitje_partner_password,
          'Api-Username':     c.eitje_api_username,
          'Api-Password':     c.eitje_api_password,
        };
        const now   = new Date();
        const start = now.toISOString().slice(0, 10);
        const end   = new Date(now.getTime() + 89 * 86400000).toISOString().slice(0, 10);
        const res   = await fetch(`${BASE_URL_HP}/leave_requests?filters[start_date]=${start}&filters[end_date]=${end}`, { method: 'GET', headers: h });
        const json  = await res.json();
        setRequests((json.items || []).filter(r => r.status === 'pending'));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (error) return null;

  const urgent  = requests.filter(r => Math.floor((Date.now() - new Date(r.created_at)) / 86400000) >= 14);
  const warning = requests.filter(r => { const d = Math.floor((Date.now() - new Date(r.created_at)) / 86400000); return d >= 10 && d < 14; });
  const normal  = requests.filter(r => Math.floor((Date.now() - new Date(r.created_at)) / 86400000) < 10);

  // Summary counts
  const headerColor = urgent.length > 0 ? C.red : warning.length > 0 ? C.amber : requests.length > 0 ? C.navy : C.green;
  const headerBg    = urgent.length > 0 ? C.redLight : warning.length > 0 ? C.amberLight : C.skyBlueLight;

  function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  function RequestRow({ r }) {
    const days = Math.floor((Date.now() - new Date(r.created_at)) / 86400000);
    const isUrgent  = days >= 14;
    const isWarning = days >= 10 && days < 14;
    const venue = EITJE_ENV_MAP_HP[r.environment?.id] || r.environment?.name || '—';
    const vi    = VENUE_IDENTITY[venue] || {};
    const dur   = Math.max(1, Math.ceil((new Date(r.end_datetime) - new Date(r.start_datetime)) / 86400000));

    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '9px 14px',
        borderLeft: `3px solid ${isUrgent ? C.red : isWarning ? C.amber : C.border}`,
        borderBottom: `1px solid ${C.border}`,
        background: isUrgent ? '#FFF8F8' : 'none',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>
            {r.user?.name || '—'}
          </div>
          <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px' }}>
            {fmtDate(r.start_datetime)} → {fmtDate(r.end_datetime)} · {dur}d
          </div>
        </div>
        {vi.short && (
          <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>
            {vi.short}
          </span>
        )}
        {isUrgent ? (
          <span style={{ fontSize: '10px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 7px', borderRadius: '20px', flexShrink: 0, whiteSpace: 'nowrap' }}>
            ⚠ {days}d overdue
          </span>
        ) : isWarning ? (
          <span style={{ fontSize: '10px', fontWeight: 700, color: C.amber, background: C.amberLight, padding: '2px 7px', borderRadius: '20px', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {days}d pending
          </span>
        ) : (
          <span style={{ fontSize: '10px', color: C.mid, flexShrink: 0 }}>{days}d</span>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={Clock}
        title="Leave Requests"
        action={onNavigate && <NavLink label="View all" onClick={() => onNavigate('team')} />}
      />
      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : requests.length === 0 ? (
        <AllClear label="No pending leave requests" />
      ) : (
        <>
          {/* Summary bar */}
          <div
            onClick={() => setExpanded(p => !p)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', cursor: 'pointer',
              background: expanded ? headerBg : 'none',
              borderBottom: expanded ? `1px solid ${C.border}` : 'none',
              transition: 'background 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '22px', fontWeight: 800, color: headerColor }}>{requests.length}</span>
              <span style={{ fontSize: '13px', color: C.mid }}>pending</span>
              {urgent.length > 0 && (
                <span style={{ fontSize: '11px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 8px', borderRadius: '20px' }}>
                  ⚠ {urgent.length} legally overdue
                </span>
              )}
              {warning.length > 0 && (
                <span style={{ fontSize: '11px', fontWeight: 700, color: C.amber, background: C.amberLight, padding: '2px 8px', borderRadius: '20px' }}>
                  {warning.length} due soon
                </span>
              )}
            </div>
            <ChevronDown size={15} color={C.mid} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
          </div>

          {/* Expanded list — urgent first, then warning, then normal */}
          {expanded && (
            <div>
              {[...urgent, ...warning, ...normal].map(r => (
                <RequestRow key={r.id} r={r} />
              ))}
              <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}` }}>
                <button onClick={() => onNavigate('team')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: C.skyText, padding: 0, display: 'flex', alignItems: 'center', gap: '3px' }}>
                  Manage in Leave Calendar <ChevronRight size={11} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Cleaning Cell Modal ───────────────────────────────────────────────────────
function CleaningCellModal({ venue, dept, items, total, onClose }) {
  const vi = VENUE_IDENTITY[venue] || {};
  const deptLabel = dept === 'bar' ? 'Bar' : 'Kitchen';
  const overdue = items.filter(i => i.isOverdue);
  const top3 = [...items].sort((a, b) => {
    if (a.days === null && b.days !== null) return -1;
    if (b.days === null && a.days !== null) return 1;
    return (b.days ?? 999) - (a.days ?? 999);
  }).slice(0, 3);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(44,58,66,0.55)', zIndex: 800, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 0 0' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: C.white, borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '480px', boxShadow: '0 -8px 32px rgba(0,0,0,0.18)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ background: vi.bg || C.cream, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${vi.border || C.border}` }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: vi.text || C.navy }}>
              {vi.short || venue} · {deptLabel}
            </div>
            <div style={{ fontSize: '11px', color: vi.text || C.mid, opacity: 0.75, marginTop: '1px' }}>
              {overdue.length} overdue of {total} items (10+ days)
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(0,0,0,0.08)', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center' }}>
            <X size={15} color={vi.text || C.navy} />
          </button>
        </div>
        {/* Top 3 list */}
        <div style={{ padding: '6px 0 4px' }}>
          {top3.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px' }}>
              <CheckCircle size={14} color={C.green} />
              <span style={{ fontSize: '13px', color: C.mid }}>All items done within 10 days</span>
            </div>
          ) : (
            top3.map((item, i) => {
              const isNever = item.days === null;
              const isVeryLate = !isNever && item.days > 17;
              const badgeColor = isNever || isVeryLate ? C.red : C.amber;
              const badgeBg    = isNever || isVeryLate ? C.redLight : C.amberLight;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: i < top3.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: C.mid, width: '20px', flexShrink: 0 }}>#{i + 1}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>{item.label}</div>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: badgeColor, background: badgeBg, padding: '2px 8px', borderRadius: '20px', flexShrink: 0 }}>
                    {isNever ? 'Never done' : `${item.days}d ago`}
                  </span>
                </div>
              );
            })
          )}
        </div>
        {/* Footer note */}
        {overdue.length > 3 && (
          <div style={{ padding: '8px 16px 14px', borderTop: `1px solid ${C.border}` }}>
            <span style={{ fontSize: '11px', color: C.mid }}>+{overdue.length - 3} more overdue — see full list in Reports</span>
          </div>
        )}
        {top3.length > 0 && overdue.length <= 3 && (
          <div style={{ padding: '8px 16px 14px', borderTop: `1px solid ${C.border}` }}>
            <span style={{ fontSize: '11px', color: C.mid }}>Tap outside to close · Full list in Reports</span>
          </div>
        )}
        {top3.length === 0 && (
          <div style={{ padding: '4px 16px 14px' }}>
            <span style={{ fontSize: '11px', color: C.mid }}>Tap outside to close</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Weekly Cleaning Card (managers/admin — matrix) ────────────────────────────
// ── Cleaning Top-3 Card (supervisor — inline list) ────────────────────────────
function WeeklyCleaningCard({ venues, onNavigate, matrixView = false }) {
  const [data,    setData]    = useState({});
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // { venue, dept }

  useEffect(() => {
    async function load() {
      if (!venues?.length) { setLoading(false); return; }
      const { data: items } = await supabase.from('cleaning_items')
        .select('id,venue,department,label').in('venue', venues).eq('active', true);
      if (!items?.length) { setLoading(false); return; }
      const ids = items.map(i => i.id);
      const { data: completions } = await supabase.from('cleaning_item_completions')
        .select('item_id,completed_at').in('item_id', ids)
        .order('completed_at', { ascending: false });
      const latestMap = {};
      (completions || []).forEach(c => { if (!latestMap[c.item_id]) latestMap[c.item_id] = c.completed_at; });

      // Build full item list per venue/dept with days + isOverdue
      const result = {};
      venues.forEach(v => { result[v] = { bar: { overdue: 0, total: 0, items: [] }, kitchen: { overdue: 0, total: 0, items: [] } }; });
      items.forEach(item => {
        const last = latestMap[item.id] || null;
        const days = last ? daysSince(last) : null;
        const isOverdue = days === null || days > 10;
        const dept = (item.department === 'bar' || item.department === 'FOH') ? 'bar' : 'kitchen';
        if (!result[item.venue]) return;
        result[item.venue][dept].total++;
        result[item.venue][dept].items.push({ label: item.label, days, isOverdue });
        if (isOverdue) result[item.venue][dept].overdue++;
      });
      setData(result);
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  if (!venues?.length) return null;

  const totalOverdue = venues.reduce((acc, v) => {
    const d = data[v] || {};
    return acc + (d.bar?.overdue || 0) + (d.kitchen?.overdue || 0);
  }, 0);

  // Cell colour helper
  function cellColor(overdue, total, show) {
    if (!show) return null;
    if (overdue === 0) return { color: C.green,   bg: C.greenLight  };
    if (overdue <= 2)  return { color: C.amber,   bg: C.amberLight  };
    return               { color: C.red,     bg: C.redLight    };
  }

  // Modal data lookup
  const modalData = modal ? (() => {
    const d = data[modal.venue]?.[modal.dept] || { items: [], total: 0 };
    return { items: d.items, total: d.total };
  })() : null;

  return (
    <>
      {modal && modalData && (
        <CleaningCellModal
          venue={modal.venue}
          dept={modal.dept}
          items={modalData.items}
          total={modalData.total}
          onClose={() => setModal(null)}
        />
      )}
      <Card>
        <CardHeader icon={Sparkles} title="Cleaning · 10-Day Overdue"
          action={onNavigate && <NavLink label="Reports" onClick={() => navigateToReportsTab('cleaning', onNavigate)} />} />
        {loading ? (
          <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
        ) : matrixView ? (
          /* ── MATRIX VIEW (admin / manager) ── */
          <>
            {totalOverdue === 0 ? (
              <AllClear label="All cleaning items done within 10 days" />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 9px', borderRadius: '20px' }}>{totalOverdue} overdue</span>
                <span style={{ fontSize: '11px', color: C.mid }}>tap a cell for top 3</span>
              </div>
            )}
            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', padding: '5px 14px', background: C.cream, borderBottom: `1px solid ${C.border}` }}>
              {['Venue', 'Bar', 'Kitchen'].map(h => (
                <span key={h} style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: h !== 'Venue' ? 'center' : 'left' }}>{h}</span>
              ))}
            </div>
            {/* Venue rows */}
            {venues.map((venue, i) => {
              const d = data[venue] || {};
              const hasFoh = FOH_CLEANING_VENUES.includes(venue);
              const barC = cellColor(d.bar?.overdue || 0, d.bar?.total || 0, hasFoh);
              const kitC = cellColor(d.kitchen?.overdue || 0, d.kitchen?.total || 0, true);
              const vi   = VENUE_IDENTITY[venue];
              return (
                <div key={venue} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', padding: '9px 14px', alignItems: 'center', borderBottom: i < venues.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>{vi?.short || venue}</span>
                  {/* Bar cell */}
                  {hasFoh ? (
                    <button
                      onClick={() => setModal({ venue, dept: 'bar' })}
                      style={{ background: barC.bg, border: 'none', borderRadius: '20px', cursor: 'pointer', padding: '3px 0', margin: '0 auto', width: '52px', textAlign: 'center' }}
                    >
                      <span style={{ fontSize: '12px', fontWeight: 700, color: barC.color }}>{d.bar?.overdue || 0}/{d.bar?.total || 0}</span>
                    </button>
                  ) : (
                    <span style={{ display: 'block', textAlign: 'center', fontSize: '12px', color: C.border }}>—</span>
                  )}
                  {/* Kitchen cell */}
                  <button
                    onClick={() => setModal({ venue, dept: 'kitchen' })}
                    style={{ background: kitC.bg, border: 'none', borderRadius: '20px', cursor: 'pointer', padding: '3px 0', margin: '0 auto', width: '52px', textAlign: 'center' }}
                  >
                    <span style={{ fontSize: '12px', fontWeight: 700, color: kitC.color }}>{d.kitchen?.overdue || 0}/{d.kitchen?.total || 0}</span>
                  </button>
                </div>
              );
            })}
          </>
        ) : (
          /* ── TOP-3 LIST VIEW (supervisor) ── */
          totalOverdue === 0 ? (
            <AllClear label="All cleaning items done within 10 days" />
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 9px', borderRadius: '20px' }}>{totalOverdue} overdue</span>
                <span style={{ fontSize: '12px', color: C.mid }}>items not done in 10+ days</span>
              </div>
              {venues.map((venue, vi) => {
                const d = data[venue] || {};
                const hasFoh = FOH_CLEANING_VENUES.includes(venue);
                const sections = [
                  ...(hasFoh && d.bar?.overdue > 0 ? [{ dept: 'Bar', key: 'bar', sd: d.bar }] : []),
                  ...(d.kitchen?.overdue > 0 ? [{ dept: 'Kitchen', key: 'kitchen', sd: d.kitchen }] : []),
                ];
                if (sections.length === 0) return null;
                const vident = VENUE_IDENTITY[venue];
                return (
                  <div key={venue} style={{ borderBottom: vi < venues.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div style={{ padding: '7px 14px', background: vident?.bg || C.cream, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 800, color: vident?.text || C.navy, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{vident?.short || venue}</span>
                      <span style={{ fontSize: '11px', color: vident?.text || C.navy, opacity: 0.7 }}>· {(d.bar?.overdue || 0) + (d.kitchen?.overdue || 0)} overdue</span>
                    </div>
                    {sections.map(({ dept, key, sd }) => {
                      const top3 = [...sd.items].filter(i => i.isOverdue).sort((a, b) => {
                        if (a.days === null && b.days !== null) return -1;
                        if (b.days === null && a.days !== null) return 1;
                        return (b.days ?? 999) - (a.days ?? 999);
                      }).slice(0, 3);
                      return (
                        <div key={dept} style={{ borderTop: `1px solid ${C.border}` }}>
                          <div style={{ padding: '5px 14px 2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{dept}</span>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: C.red, background: C.redLight, padding: '1px 6px', borderRadius: '20px' }}>{sd.overdue}/{sd.total}</span>
                          </div>
                          {top3.map((item, j) => (
                            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px 6px 28px', borderTop: j > 0 ? `1px solid ${C.border}` : 'none' }}>
                              <span style={{ fontSize: '11px', color: C.mid, flexShrink: 0 }}>#{j + 1}</span>
                              <span style={{ fontSize: '12px', color: C.charcoal, flex: 1 }}>{item.label}</span>
                              <span style={{ fontSize: '11px', fontWeight: 700, flexShrink: 0, color: item.days === null ? C.red : item.days > 17 ? C.red : C.amber }}>
                                {item.days === null ? 'Never' : `${item.days}d`}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )
        )}
      </Card>
    </>
  );
}

// ── Pest Check Card (managers/admin/supervisor) ───────────────────────────────
function PestCheckCard({ venues, onNavigate }) {
  const [checks,  setChecks]  = useState([]);
  const [loading, setLoading] = useState(true);
  const today = todayStr();
  const pestVenues = (venues || []).filter(v => PEST_CHECK_VENUES.includes(v));

  useEffect(() => {
    async function load() {
      if (!pestVenues.length) { setLoading(false); return; }
      const { data } = await supabase.from('pest_check_log')
        .select('venue,department,completed_by,completed_at')
        .eq('date', today).in('venue', pestVenues);
      setChecks(data || []); setLoading(false);
    }
    load();
  }, [pestVenues.join(',')]);

  if (!pestVenues.length) return null;
  const statusMap = {};
  pestVenues.forEach(v => { statusMap[v] = { bar: null, kitchen: null }; });
  checks.forEach(c => { if (statusMap[c.venue]) statusMap[c.venue][c.department] = c; });

  return (
    <Card>
      <CardHeader icon={Bug} title="Pest Check · Today"
        action={onNavigate && <NavLink label="Reports" onClick={() => navigateToReportsTab('cleaning', onNavigate)} />} />
      {loading ? <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', padding: '6px 14px', background: C.cream, borderBottom: `1px solid ${C.border}` }}>
            {['Venue','Bar','Kitchen'].map(h => <span key={h} style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: h !== 'Venue' ? 'center' : 'left' }}>{h}</span>)}
          </div>
          <div style={{ padding: '4px 0' }}>
            {pestVenues.map((venue, i) => {
              const vi = VENUE_IDENTITY[venue], s = statusMap[venue], hasBar = venue !== 'Prep Kitchen';
              const Tick = ({ entry, show }) => {
                if (!show) return <span style={{ display: 'block', textAlign: 'center', color: C.border, fontSize: '13px' }}>—</span>;
                const t = entry ? new Date(entry.completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
                return <span title={entry ? `${entry.completed_by} · ${t}` : ''} style={{ display: 'block', textAlign: 'center', fontSize: '16px', cursor: entry ? 'help' : 'default' }}>{entry ? '✅' : '❌'}</span>;
              };
              return (
                <div key={venue} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', padding: '8px 14px', alignItems: 'center', borderBottom: i < pestVenues.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>{vi?.short || venue}</span>
                  <Tick entry={s.bar} show={hasBar} />
                  <Tick entry={s.kitchen} show={true} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
}

// ── Pest Check Banner (bar/kitchen staff) ─────────────────────────────────────
function PestCheckBanner({ currentUser, onNavigate }) {
  const [done,  setDone]  = useState(null);
  const venue  = currentUser?.venue;
  const dept   = currentUser?.role === 'kitchen' ? 'kitchen' : 'bar';
  const today  = todayStr();
  const isPest = PEST_CHECK_VENUES.includes(venue);

  useEffect(() => {
    if (!isPest) return;
    supabase.from('pest_check_log').select('id,completed_by,completed_at')
      .eq('venue', venue).eq('department', dept).eq('date', today).maybeSingle()
      .then(({ data }) => setDone(data || false));
  }, [venue, dept, today, isPest]);

  if (!isPest || done === null) return null;

  if (done) {
    const time = new Date(done.completed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return (
      <div style={{ background: C.greenLight, border: `1px solid ${C.green}44`, borderLeft: `3px solid ${C.green}`, borderRadius: '12px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '18px' }}>✅</span>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: C.green }}>Pest check done</div>
          <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px' }}>{done.completed_by} · {time}</div>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => onNavigate('checklist')} style={{ width: '100%', background: C.redLight, border: `2px solid ${C.red}`, borderRadius: '12px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'left' }}>
      <Bug size={20} color={C.red} strokeWidth={2} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 800, color: C.red }}>Pest check not done</div>
        <div style={{ fontSize: '12px', color: C.mid, marginTop: '2px' }}>Check all pest-prone areas — tap to complete</div>
      </div>
      <ChevronRight size={16} color={C.red} />
    </button>
  );
}

// ── Staff Alerts Card (bar/kitchen) ───────────────────────────────────────────
function StaffAlertsCard({ currentUser, products, requests, onNavigate }) {
  const venue    = currentUser?.venue;
  const DAYS     = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const todayDay = DAYS[new Date().getDay()];
  const today    = todayStr();
  if (!venue) return null;

  const hasStocktake = (products || []).some(p =>
    p.active && (p.available_at || p.availableAt || []).includes(venue) &&
    (p.order_days || p.orderDays || []).includes(todayDay)
  );
  const deliveries = (requests || []).filter(r =>
    r.venue === venue && ['pending','en-route','partially fulfilled'].includes(r.status) && r.delivery_date === today
  );
  const pendingIn = (requests || []).filter(r =>
    r.venue === venue && (r.orderFrom || r.order_from) !== 'Prep Kitchen' &&
    ['pending','en-route','partially fulfilled'].includes(r.status)
  );
  const pendingOut = (requests || []).filter(r =>
    (r.orderFrom || r.order_from) === venue && r.status === 'pending'
  );
  const hasAlerts = hasStocktake || deliveries.length > 0 || pendingIn.length > 0 || pendingOut.length > 0;
  if (!hasAlerts) return null;

  const Row = ({ icon: Icon, color, bg, label, sub, onClick }) => (
    <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: bg, border: 'none', borderBottom: `1px solid ${color}22`, cursor: 'pointer', textAlign: 'left' }}>
      <Icon size={14} color={color} strokeWidth={2} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color }}>{label}</div>
        {sub && <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px' }}>{sub}</div>}
      </div>
      <ChevronRight size={13} color={color} />
    </button>
  );

  return (
    <Card>
      <CardHeader icon={AlertTriangle} title="Alerts · Today" />
      <div style={{ padding: '4px 0' }}>
        {hasStocktake && <Row icon={ClipboardList} color={C.green} bg={C.greenLight} label="Stocktake day" sub="Submit tonight for tomorrow's delivery" onClick={() => onNavigate('stocktake')} />}
        {deliveries.map((r, i) => <Row key={`d${i}`} icon={Package} color={C.green} bg={C.greenLight} label="Delivery expected today" sub={r.status} onClick={() => onNavigate('transfers')} />)}
        {pendingIn.map((r, i) => <Row key={`in${i}`} icon={Package} color={C.green} bg={C.greenLight} label="Incoming transfer" sub={`From ${r.orderFrom || r.order_from} · ${r.status}`} onClick={() => onNavigate('transfers')} />)}
        {pendingOut.map((r, i) => <Row key={`out${i}`} icon={Package} color={C.amber} bg={C.amberLight} label="Transfer requested from you" sub={`${r.venue} is waiting for stock`} onClick={() => onNavigate('transfers')} />)}
      </div>
    </Card>
  );
}

// ── Staff Checklist Link Card ─────────────────────────────────────────────────
function StaffChecklistCard({ onNavigate }) {
  return (
    <Card>
      <CardHeader icon={CheckCircle} title="Today's Checklist" />
      <button onClick={() => onNavigate('checklist')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        onMouseEnter={e => e.currentTarget.style.background = C.cream}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>Go to checklist</div>
          <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>Complete your shift and cleaning tasks</div>
        </div>
        <ChevronRight size={14} color={C.mid} />
      </button>
    </Card>
  );
}

// ── Staff Cleaning Link Card ──────────────────────────────────────────────────
function StaffCleaningCard({ currentUser, onNavigate }) {
  const [overdueCount, setOverdueCount] = useState(null);
  const [topItems,     setTopItems]     = useState([]);
  const venue = currentUser?.venue;
  // Support both naming conventions: 'bar'/'kitchen' and 'FOH'/'KIT'
  const deptValues = currentUser?.role === 'kitchen' ? ['kitchen', 'KIT'] : ['bar', 'FOH'];

  useEffect(() => {
    async function load() {
      if (!venue) return;
      const { data: items } = await supabase.from('cleaning_items')
        .select('id,label,priority').eq('venue', venue).in('department', deptValues).eq('active', true);
      if (!items?.length) { setOverdueCount(0); setTopItems([]); return; }
      const { data: completions } = await supabase.from('cleaning_item_completions')
        .select('item_id,completed_at').in('item_id', items.map(i => i.id))
        .order('completed_at', { ascending: false });
      const latest = {};
      (completions || []).forEach(c => { if (!latest[c.item_id]) latest[c.item_id] = c.completed_at; });
      const overdueItems = items
        .map(item => {
          const l = latest[item.id];
          const days = l ? daysSince(l) : null;
          return { ...item, days, isOverdue: !l || days > 10 };
        })
        .filter(it => it.isOverdue)
        .sort((a, b) => {
          // Never done first, then by days desc
          if (a.days === null && b.days !== null) return -1;
          if (b.days === null && a.days !== null) return 1;
          return (b.days ?? 999) - (a.days ?? 999);
        });
      setOverdueCount(overdueItems.length);
      setTopItems(overdueItems.slice(0, 3));
    }
    load();
  }, [venue, deptValues.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card>
      <CardHeader icon={Sparkles} title="Cleaning Checklist"
        action={<NavLink label="View all" onClick={() => onNavigate('checklist')} />} />
      <button onClick={() => onNavigate('checklist')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: topItems.length > 0 ? `1px solid ${C.border}` : 'none' }}
        onMouseEnter={e => e.currentTarget.style.background = C.cream}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
        <div style={{ flex: 1 }}>
          {overdueCount === null ? <div style={{ fontSize: '13px', color: C.mid }}>Loading…</div>
          : overdueCount === 0 ? <>
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.green }}>All items up to date</div>
              <div style={{ fontSize: '11px', color: C.mid, marginTop: '2px' }}>No overdue cleaning tasks</div>
            </>
          : <>
              <span style={{ fontSize: '13px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 9px', borderRadius: '20px' }}>{overdueCount} overdue</span>
              <div style={{ fontSize: '11px', color: C.mid, marginTop: '4px' }}>Items not done in 10+ days — tap to view all</div>
            </>
          }
        </div>
        <ChevronRight size={14} color={C.mid} />
      </button>
      {topItems.length > 0 && (
        <div style={{ padding: '4px 0' }}>
          {topItems.map((item, i) => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: i < topItems.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, color: C.mid, width: '22px', flexShrink: 0 }}>#{i + 1}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</div>
              </div>
              <span style={{ fontSize: '11px', fontWeight: 700, flexShrink: 0, color: item.days === null ? C.red : item.days > 17 ? C.red : C.amber }}>
                {item.days === null ? 'Never done' : `${item.days}d ago`}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── Cash Variance ─────────────────────────────────────────────────────────────
function CashVariance({ venues }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      let q = supabase.from('cashup_log').select('venue,variance,date').eq('date', yesterdayStr());
      if (venues?.length) q = q.in('venue', venues);
      const { data } = await q;
      setEntries((data || []).filter(e => Math.abs(e.variance || 0) > 10));
      setLoading(false);
    }
    load();
  }, [venues?.join(',')]);

  return (
    <Card>
      <CardHeader icon={ShieldAlert} title="Cash Variance · Yesterday" />
      {loading ? (
        <div style={{ padding: '11px 14px', color: C.mid, fontSize: '13px' }}>Loading…</div>
      ) : entries.length === 0 ? (
        <AllClear label="No significant variance yesterday" />
      ) : (
        <div style={{ padding: '4px 0' }}>
          {entries.map((e, i) => {
            const vi    = VENUE_IDENTITY[e.venue];
            const abs   = Math.abs(e.variance);
            const isPos = e.variance > 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', borderBottom: i < entries.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal }}>{e.venue}</div>
                </div>
                <span style={{ fontSize: '13px', fontWeight: 800, color: isPos ? C.green : C.red }}>
                  {isPos ? '+' : '-'}€{abs.toFixed(2)}
                </span>
                {vi && <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>{vi.short}</span>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ── EventsCard ────────────────────────────────────────────────────────────────
// Paste this function into Homepage.js just before the // ── Main comment
// Add to role sections:
//   Admin/Manager:    <EventsCard onNavigate={onNavigate} canEdit={true} />
//   Supervisor/Staff: <EventsCard onNavigate={onNavigate} canEdit={false} />

function EventsCard({ onNavigate, canEdit }) {
  const [counts,   setCounts]   = useState({ enquiry_sent: 0, form_submitted: 0, confirmed: 0 });
  const [upcoming, setUpcoming] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    async function load() {
      const today = new Date().toISOString().split('T')[0];
      let q = supabase.from('group_events')
        .select('id,status,contact_name,event_date,venue,guest_count,event_type')
        .neq('status', 'cancelled');
      if (!canEdit) q = q.eq('status', 'confirmed');
      const { data } = await q;
      const all = data || [];

      const c = { enquiry_sent: 0, form_submitted: 0, confirmed: 0 };
      all.forEach(e => { if (c[e.status] !== undefined) c[e.status]++; });
      setCounts(c);

      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() + 60);
      setUpcoming(
        all
          .filter(e => e.status === 'confirmed' && e.event_date >= today && e.event_date <= cutoff.toISOString().split('T')[0])
          .sort((a, b) => a.event_date.localeCompare(b.event_date))
          .slice(0, 6)
      );
      setLoading(false);
    }
    load();
  }, [canEdit]);

  const hasAction = canEdit && counts.form_submitted > 0;

  return (
    <Card>
      {/* Header row */}
      <div
        onClick={() => onNavigate('events')}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: expanded ? `1px solid ${C.border}` : 'none', background: C.cream, cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background = '#EEF4F7'}
        onMouseLeave={e => e.currentTarget.style.background = C.cream}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Calendar size={13} color={C.navy} strokeWidth={2.2} />
          <span style={{ fontSize: '11px', fontWeight: 700, color: C.navy, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Events</span>
          {hasAction && (
            <span style={{ fontSize: '10px', fontWeight: 800, color: C.amber, background: C.amberLight, padding: '1px 7px', borderRadius: '20px' }}>
              {counts.form_submitted} action{counts.form_submitted > 1 ? 's' : ''}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {loading ? (
            <span style={{ fontSize: '12px', color: C.mid }}>—</span>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {canEdit && (
                <>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: counts.enquiry_sent > 0 ? C.skyText : C.mid }}>
                    {counts.enquiry_sent} sent
                  </span>
                  <span style={{ color: C.border }}>·</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: counts.form_submitted > 0 ? C.amber : C.mid }}>
                    {counts.form_submitted} action
                  </span>
                  <span style={{ color: C.border }}>·</span>
                </>
              )}
              <span style={{ fontSize: '11px', fontWeight: 700, color: counts.confirmed > 0 ? C.green : C.mid }}>
                {counts.confirmed} confirmed
              </span>
            </div>
          )}
          <button
            onClick={e => { e.stopPropagation(); setExpanded(p => !p); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.mid, padding: '2px', display: 'flex', alignItems: 'center' }}
          >
            <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
        </div>
      </div>

      {/* Expanded list — upcoming confirmed only */}
      {expanded && (
        <div style={{ padding: '4px 0' }}>
          {upcoming.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '10px 14px' }}>
              <CheckCircle size={13} color={C.green} />
              <span style={{ fontSize: '13px', color: C.mid }}>No upcoming confirmed events</span>
            </div>
          ) : (
            upcoming.map((ev, i) => {
              const vi   = VENUE_IDENTITY[ev.venue] || {};
              const t    = new Date(); t.setHours(0,0,0,0);
              const d    = new Date(ev.event_date + 'T00:00:00'); d.setHours(0,0,0,0);
              const days = Math.floor((d - t) / 86400000);
              const dayLabel = days === 0 ? 'Today' : days === 1 ? 'Tomorrow'
                : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              return (
                <div key={ev.id}
                  onClick={() => onNavigate('events')}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderBottom: i < upcoming.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = C.cream}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ minWidth: '64px', flexShrink: 0 }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: days <= 7 ? C.amber : C.mid }}>{dayLabel}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: C.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.contact_name || 'Group Event'}{ev.guest_count ? ` · ${ev.guest_count}pp` : ''}
                    </div>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, color: vi.text, background: vi.bg, padding: '2px 7px', borderRadius: '20px', flexShrink: 0 }}>
                    {vi.short || ev.venue}
                  </span>
                </div>
              );
            })
          )}
          <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}` }}>
            <button onClick={() => onNavigate('events')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: 700, color: C.skyText, padding: 0, display: 'flex', alignItems: 'center', gap: '3px' }}>
              View all events <ChevronRight size={11} />
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Homepage({ currentUser, onNavigate, products = [], requests = [] }) {
  const isMobile  = useIsMobile();
  const [teamMembers, setTeamMembers] = useState([]);

  const role    = currentUser?.role;
  const isAdmin = role === 'admin';
  const isMgr   = role === 'manager';
  const isSup   = role === 'supervisor';
  const isStaff = role === 'bar' || role === 'kitchen';
  const dept    = role === 'bar' ? 'FOH' : role === 'kitchen' ? 'KIT' : null;

  const userVenues = isAdmin
    ? ['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen']
    : isMgr
    ? (currentUser.venues?.length ? currentUser.venues : [currentUser.venue])
    : [currentUser.venue];

  const now      = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = currentUser?.name?.split(' ')[0] || 'there';

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('team_members')
        .select('id,name,venue,dept,birthday,active').eq('active', true);
      setTeamMembers(data || []);
    }
    load();
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#EAF4F8' }}>
      <div style={{ padding: isMobile ? '1rem' : '24px', maxWidth: '720px', margin: '0 auto' }}>

        {/* Greeting */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: isMobile ? '20px' : '22px', fontWeight: 800, color: C.navy }}>
            {greeting}, {firstName}
          </div>
          <div style={{ fontSize: '13px', color: C.skyText, marginTop: '2px', fontWeight: 500 }}>
            {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>

          {/* ── ADMIN ─────────────────────────────────────── */}
          {isAdmin && <>
            <MTDCards venues={['Box Jordaan', 'Box Plantage', 'Sassys']} isMobile={isMobile} onNavigate={onNavigate} />
            <ShiftyYesterday venues={null} />
            <UpcomingEvents venues={['All Venues']} teamMembers={teamMembers} onNavigate={onNavigate} />
            <ContractsEnding venues={null} onNavigate={onNavigate} />
            <NewStaffOnboarding venues={null} onNavigate={onNavigate} />
            <LeaveRequestsCard onNavigate={onNavigate} />
            <MaintenanceWidget venues={null} currentUser={currentUser} onNavigate={onNavigate} />
            <ReviewsCard venues={['Box Jordaan', 'Box Plantage', 'Sassys']} onNavigate={onNavigate} />
            <CashVariance venues={null} />
            <ChecklistStatus venues={['Box Jordaan', 'Box Plantage', 'Sassys']} onNavigate={onNavigate} />
            <WeeklyCleaningCard venues={['Box Jordaan', 'Box Plantage', 'Sassys', 'Prep Kitchen']} onNavigate={onNavigate} matrixView={true} />
            <PestCheckCard venues={['Box Jordaan', 'Box Plantage', 'Prep Kitchen']} onNavigate={onNavigate} />
            <FridgeTempAlert venues={null} />
          </>}

          {/* ── MANAGER ───────────────────────────────────── */}
          {isMgr && <>
            <MTDCards venues={userVenues} isMobile={isMobile} onNavigate={onNavigate} />
            <ShiftyYesterday venues={userVenues} />
            <UpcomingEvents venues={[...userVenues, 'All Venues']} teamMembers={teamMembers} onNavigate={onNavigate} />
            <ChecklistStatus venues={userVenues} onNavigate={onNavigate} />
            <WeeklyCleaningCard venues={userVenues} onNavigate={onNavigate} matrixView={true} />
            <ContractsEnding venues={userVenues} onNavigate={onNavigate} />
            <NewStaffOnboarding venues={userVenues} onNavigate={onNavigate} />
            <LeaveRequestsCard onNavigate={onNavigate} />
            <CashVariance venues={userVenues} />
            <ReviewsCard venues={userVenues} onNavigate={onNavigate} />
            <MaintenanceWidget venues={userVenues} currentUser={currentUser} onNavigate={onNavigate} />
            <PestCheckCard venues={userVenues.filter(v => PEST_CHECK_VENUES.includes(v))} onNavigate={onNavigate} />
            <FridgeTempAlert venues={userVenues} />
          </>}

          {/* ── SUPERVISOR ────────────────────────────────── */}
          {isSup && <>
            <ShiftyYesterday venues={[currentUser.venue]} />
            <UpcomingEvents venues={[currentUser.venue, 'All Venues']} teamMembers={teamMembers} onNavigate={onNavigate} />
            <NewStaffOnboarding venues={[currentUser.venue]} onNavigate={onNavigate} />
            <LeaveRequestsCard onNavigate={onNavigate} />
            <ChecklistStatus venues={[currentUser.venue]} onNavigate={onNavigate} />
            <WeeklyCleaningCard venues={[currentUser.venue]} onNavigate={onNavigate} />
            <CashVariance venues={[currentUser.venue]} />
            <PestCheckCard venues={[currentUser.venue].filter(v => PEST_CHECK_VENUES.includes(v))} onNavigate={onNavigate} />
            <ReviewsCard venues={[currentUser.venue]} onNavigate={onNavigate} />
            <MaintenanceWidget venues={[currentUser.venue]} currentUser={currentUser} onNavigate={onNavigate} />
          </>}

          {/* ── ADVISOR ───────────────────────────────────── */}
          {role === 'advisor' && <>
            <MTDCards venues={['Box Jordaan', 'Box Plantage', 'Sassys']} isMobile={isMobile} onNavigate={onNavigate} />
          </>}

          {/* ── BAR / KITCHEN ─────────────────────────────── */}
          {isStaff && <>
            <PestCheckBanner currentUser={currentUser} onNavigate={onNavigate} />
            <StaffAlertsCard currentUser={currentUser} products={products} requests={requests} onNavigate={onNavigate} />
            <StaffChecklistCard onNavigate={onNavigate} />
            <ReviewsCard venues={[currentUser.venue]} onNavigate={onNavigate} />
            <WeeklyCleaningCard venues={[currentUser.venue]} onNavigate={onNavigate} />
          </>}

        </div>
      </div>
    </div>
  );
}